import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useLanguage } from "../contexts/LanguageContext";


// ======================================================
// MOVIEFRENZY — PALMARÈS CINÉMA
//
// VERSION REFACTORISÉE
//
// - bouton toujours présent
// - chargement en arrière-plan
// - Wikidata + Wikipédia en parallèle
// - sections Wikipédia en parallèle
// - fusion des deux sources
// - dédoublonnage
// - priorité aux entrées les plus riches
// - cache par acteur + langue
// - support FR / EN / ES / DE
// - support Québec : Iris / Jutra / Génie / Québec Cinéma
// - aucune donnée de démonstration
//
// ======================================================


// ======================================================
// ENDPOINTS
// ======================================================

const WIKIDATA_SPARQL_ENDPOINT =
  "https://query.wikidata.org/sparql";

const WIKIDATA_API_ENDPOINT =
  "https://www.wikidata.org/w/api.php";

const WIKIPEDIA_API_ENDPOINTS = {
  fr: "https://fr.wikipedia.org/w/api.php",
  en: "https://en.wikipedia.org/w/api.php",
  es: "https://es.wikipedia.org/w/api.php",
  de: "https://de.wikipedia.org/w/api.php",
};


// ======================================================
// CACHE
// ======================================================
//
// Important :
//
// Si tu passes de Roy Dupuis à un autre acteur puis reviens
// à Roy Dupuis, on ne recommence pas toutes les requêtes.
//
// Le cache vit tant que l'application React reste ouverte.
// ======================================================

const PALMARES_CACHE = new Map();


// ======================================================
// TEXTES UI
// ======================================================

const PALMARES_COPY = {
  fr: {
    trigger: "Palmarès cinéma",
    title: "Palmarès cinéma",
    wins: "Prix remportés",
    nominations: "Nominations",
    movie: "Film concerné",

    loading:
      "Recherche du palmarès cinéma…",

    empty:
      "Aucun palmarès cinéma exploitable n’a été trouvé dans les sources consultées.",

    error:
      "Le palmarès cinéma n’a pas pu être récupéré pour le moment.",

    close:
      "Fermer le palmarès cinéma",

    sourceWikidata:
      "Source : Wikidata",

    sourceWikipedia:
      "Source : Wikipédia",

    sourceBoth:
      "Sources : Wikidata + Wikipédia",
  },

  en: {
    trigger: "Film awards",
    title: "Film awards",
    wins: "Awards won",
    nominations: "Nominations",
    movie: "Related film",

    loading:
      "Searching film awards…",

    empty:
      "No usable film awards were found in the consulted sources.",

    error:
      "Film awards could not be retrieved at the moment.",

    close:
      "Close film awards",

    sourceWikidata:
      "Source: Wikidata",

    sourceWikipedia:
      "Source: Wikipedia",

    sourceBoth:
      "Sources: Wikidata + Wikipedia",
  },

  es: {
    trigger: "Palmarés de cine",
    title: "Palmarés de cine",
    wins: "Premios ganados",
    nominations: "Nominaciones",
    movie: "Película relacionada",

    loading:
      "Buscando el palmarés cinematográfico…",

    empty:
      "No se encontró un palmarés cinematográfico utilizable en las fuentes consultadas.",

    error:
      "No se pudo recuperar el palmarés cinematográfico por el momento.",

    close:
      "Cerrar el palmarés",

    sourceWikidata:
      "Fuente: Wikidata",

    sourceWikipedia:
      "Fuente: Wikipedia",

    sourceBoth:
      "Fuentes: Wikidata + Wikipedia",
  },

  de: {
    trigger: "Film-Auszeichnungen",
    title: "Film-Auszeichnungen",
    wins: "Gewonnene Preise",
    nominations: "Nominierungen",
    movie: "Zugehöriger Film",

    loading:
      "Film-Auszeichnungen werden gesucht…",

    empty:
      "In den abgefragten Quellen wurden keine verwertbaren Film-Auszeichnungen gefunden.",

    error:
      "Die Film-Auszeichnungen konnten momentan nicht geladen werden.",

    close:
      "Film-Auszeichnungen schließen",

    sourceWikidata:
      "Quelle: Wikidata",

    sourceWikipedia:
      "Quelle: Wikipedia",

    sourceBoth:
      "Quellen: Wikidata + Wikipedia",
  },
};


// ======================================================
// NORMALISATION
// ======================================================

const normalizeText = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[’‘`´]/g, "'")
    .replace(/[–—]/g, "-")
    .replace(/\s+/g, " ")
    .trim();


const cleanText = (value = "") =>
  String(value)
    .replace(/\[[^\]]*\]/g, "")
    .replace(/[†‡]/g, "")
    .replace(/\s+/g, " ")
    .replace(/\s+([,.;:!?])/g, "$1")
    .trim();


const getEntityIdFromUrl = (url = "") => {
  const match =
    String(url).match(
      /\/entity\/(Q\d+)$/i
    );

  return match
    ? match[1]
    : null;
};


const uniqueStrings = (values = []) =>
  Array.from(
    new Set(
      values.filter(Boolean)
    )
  );


// ======================================================
// LANGUE
// ======================================================

const normalizeLanguageKey = (
  language
) => {
  const key =
    String(
      language || "en"
    )
      .split("-")[0]
      .toLowerCase();

  return [
    "fr",
    "en",
    "es",
    "de",
  ].includes(key)
    ? key
    : "en";
};


const getLanguagePriority = (
  languageKey
) =>
  uniqueStrings([
    languageKey,
    "fr",
    "en",
    "es",
    "de",
  ]);


// ======================================================
// RECONNAISSANCE CINÉMA
// ======================================================
//
// On ne cherche PAS à récupérer toutes les récompenses
// d'une personne.
//
// On veut le PALMARÈS CINÉMA.
//
// Donc :
// - Oscars : oui
// - Cannes : oui
// - Jutra / Iris : oui
// - Génie : oui
// - Golden Globe cinéma : oui
//
// mais :
// - Emmy TV : non
// - Grammy musique : non
// - théâtre : non
// - jeux vidéo : non
//
// ======================================================


// ------------------------------------------------------
// PRIX QUÉBÉCOIS / CANADIENS
// ------------------------------------------------------

const QUEBEC_CINEMA_TERMS = [
  "prix iris",
  "iris-hommage",
  "iris hommage",

  "prix jutra",
  "jutra",
  "jutras",
  "soiree des jutras",
  "soirée des jutras",

  "gala quebec cinema",
  "gala québec cinéma",
  "quebec cinema",
  "québec cinéma",

  "prix genie",
  "prix génie",
  "genie award",
  "genie awards",
  "soiree des genie",
  "soirée des génie",

  "canadian screen award",
  "canadian screen awards",

  "prix ecrans canadiens",
  "prix écrans canadiens",
];


// ------------------------------------------------------
// PRIX CINÉMA RECONNUS
// ------------------------------------------------------

const FILM_AWARD_TERMS = [

  // OSCARS
  "academy award",
  "academy awards",
  "oscar",

  // CANNES
  "festival de cannes",
  "cannes film festival",
  "palme d'or",
  "palme dor",
  "prix d'interpretation",
  "prix d’interprétation",

  // BAFTA
  "bafta",
  "british academy film",

  // GOLDEN GLOBES
  "golden globe",

  // QUÉBEC / CANADA
  ...QUEBEC_CINEMA_TERMS,

  // FRANCE
  "cesar",
  "césar",
  "lumiere award",
  "prix lumiere",
  "prix lumière",

  // VENISE
  "venice film festival",
  "mostra de venise",
  "volpi cup",
  "coupe volpi",

  // BERLIN
  "berlin international film festival",
  "berlinale",
  "silver bear",
  "golden bear",
  "ours d'argent",
  "ours d’or",
  "ours d'or",

  // EUROPE
  "european film award",

  // ESPAGNE
  "goya award",
  "premio goya",

  // ITALIE
  "david di donatello",
  "nastro d'argento",
  "nastro d’argento",

  // USA / INTERNATIONAL
  "independent spirit award",
  "critics choice movie",
  "critics' choice movie",
  "critics choice award",
  "screen actors guild award",
  "sag award",

  "national society of film critics",
  "new york film critics",
  "los angeles film critics",
  "london film critics",

  "national board of review",

  "empire award",
  "empire awards",

  "young artist award",
  "young artist awards",

  "mtv movie award",
  "mtv movie awards",

  // GÉNÉRIQUES
  "film award",
  "film awards",
  "movie award",
  "movie awards",

  "cinema award",
  "cinema awards",

  "cinematic award",

  "prix cinematographique",
  "prix cinématographique",

  "premio cinematografico",
  "premio cinematográfico",

  "filmpreis",
];


// ------------------------------------------------------
// SIGNAUX CINÉMA FORTS
// ------------------------------------------------------

const STRONG_FILM_TERMS = [
  "film",
  "movie",
  "motion picture",

  "cinema",
  "cinéma",
  "cinematic",

  "cinematographique",
  "cinématographique",

  "pelicula",
  "película",
  "cine",

  "filmpreis",

  "acteur",
  "actrice",
  "actor",
  "actress",

  "supporting actor",
  "supporting actress",

  "meilleur acteur",
  "meilleure actrice",

  "best actor",
  "best actress",

  "best supporting actor",
  "best supporting actress",
];


// ------------------------------------------------------
// ÉLÉMENTS À EXCLURE
// ------------------------------------------------------

const NON_FILM_TERMS = [

  // TV
  "television",
  "télévision",
  "televisión",
  "televisione",

  "tv series",
  "television series",
  "television movie",

  "serie televisee",
  "série télévisée",

  "serie de television",
  "serie de televisión",

  "fernsehserie",
  "fernsehen",

  "miniseries",
  "mini-series",
  "mini-série",

  // MUSIQUE
  "grammy",
  "music award",
  "music prize",
  "musique",

  // THÉÂTRE
  "theatre",
  "théâtre",
  "broadway",
  "stage award",

  "soiree des masques",
  "soirée des masques",

  // RADIO
  "radio award",

  // JEUX
  "video game",
  "videogame",
  "jeu video",
  "jeu vidéo",
];


// ======================================================
// HELPERS FILTRAGE
// ======================================================

const containsAnyTerm = (
  text,
  terms
) => {
  const normalized =
    normalizeText(text);

  return terms.some(
    (term) =>
      normalized.includes(
        normalizeText(term)
      )
  );
};


const isQuebecCinemaAward = (
  text
) =>
  containsAnyTerm(
    text,
    QUEBEC_CINEMA_TERMS
  );


const isCinemaAward = ({
  awardLabel = "",
  awardGroupLabel = "",
  relatedWorkLabel = "",
}) => {
  const combined = [
    awardLabel,
    awardGroupLabel,
    relatedWorkLabel,
  ]
    .filter(Boolean)
    .join(" ");

  if (!combined) {
    return false;
  }

  const hasRecognizedAward =
    containsAnyTerm(
      combined,
      FILM_AWARD_TERMS
    );

  const hasStrongFilmTerm =
    containsAnyTerm(
      combined,
      STRONG_FILM_TERMS
    );

  const hasNonFilmTerm =
    containsAnyTerm(
      combined,
      NON_FILM_TERMS
    );

  // ======================================================
  // GARDE-FOU ABSOLU : HORS CINÉMA
  // ======================================================
  //
  // Si le texte indique explicitement :
  // télévision, série TV, mini-série,
  // musique, théâtre, radio ou jeu vidéo,
  // on rejette immédiatement.
  //
  // Exemple :
  // "Golden Globe du meilleur acteur dans une
  // série télévisée dramatique"
  // => REJETÉ
  //
  // Aucun signal générique comme "acteur",
  // "Golden Globe" ou "film" ne peut le sauver.
  // ======================================================

  if (hasNonFilmTerm) {
    return false;
  }

  // ======================================================
  // QUÉBEC / CANADA
  // ======================================================
  //
  // Prix Iris, Jutra, Génie,
  // Gala Québec Cinéma, etc.
  //
  // On effectue cette validation APRÈS
  // le garde-fou hors cinéma.
  // ======================================================

  if (
    isQuebecCinemaAward(
      combined
    )
  ) {
    return true;
  }

  // ======================================================
  // AUTRES PRIX CINÉMA
  // ======================================================
  //
  // On conserve :
  // - un prix cinéma explicitement reconnu
  // - OU un signal cinéma suffisamment fort
  // ======================================================

  return (
    hasRecognizedAward ||
    hasStrongFilmTerm
  );
};


// ======================================================
// FETCH GÉNÉRIQUE
// ======================================================

const fetchJson = async (
  url,
  {
    signal,
    headers = {},
  } = {}
) => {
  const response =
    await fetch(
      url,
      {
        signal,

        headers: {
          Accept: "application/json",
          ...headers,
        },
      }
    );

  if (!response.ok) {
    throw new Error(
      `HTTP ${response.status}`
    );
  }

  return response.json();
};


// ======================================================
// WIKIDATA — SPARQL
// ======================================================

const fetchSparql = async (
  query,
  signal
) => {
  const url =
    `${WIKIDATA_SPARQL_ENDPOINT}` +
    `?query=${encodeURIComponent(query)}` +
    "&format=json";

  const response =
    await fetch(
      url,
      {
        signal,

        headers: {
          Accept:
            "application/sparql-results+json",
        },
      }
    );

  if (!response.ok) {
    throw new Error(
      `Wikidata SPARQL HTTP ${response.status}`
    );
  }

  return response.json();
};


// ======================================================
// WIKIDATA — TROUVER LA PERSONNE PAR TMDB
// ======================================================

const findPersonFromTmdbId = async (
  actorId,
  signal
) => {
  const tmdbId =
    String(
      actorId || ""
    )
      .replace(
        /[^\d]/g,
        ""
      );

  if (!tmdbId) {
    return null;
  }


  const query = `
    PREFIX wdt:
      <http://www.wikidata.org/prop/direct/>

    SELECT DISTINCT ?person
    WHERE {
      ?person
        wdt:P4985
        "${tmdbId}".
    }

    LIMIT 1
  `;


  const data =
    await fetchSparql(
      query,
      signal
    );


  const personUrl =
    data
      ?.results
      ?.bindings
      ?.[0]
      ?.person
      ?.value;


  return getEntityIdFromUrl(
    personUrl
  );
};


// ======================================================
// WIKIDATA — FALLBACK PAR NOM
// ======================================================

const searchWikidataByName =
  async ({
    actorName,
    languageKey,
    signal,
  }) => {

    const name =
      cleanText(
        actorName
      );

    if (!name) {
      return null;
    }


    const params =
      new URLSearchParams({
        action:
          "wbsearchentities",

        search:
          name,

        language:
          languageKey,

        uselang:
          languageKey,

        type:
          "item",

        limit:
          "8",

        format:
          "json",

        origin:
          "*",
      });


    const data =
      await fetchJson(
        `${WIKIDATA_API_ENDPOINT}?${params.toString()}`,
        {
          signal,
        }
      );


    const candidates =
      Array.isArray(
        data?.search
      )
        ? data.search
        : [];


    if (!candidates.length) {
      return null;
    }


    const candidateIds =
      candidates
        .map(
          (item) =>
            item?.id
        )
        .filter(
          (id) =>
            /^Q\d+$/.test(
              id || ""
            )
        )
        .slice(
          0,
          8
        );


    if (!candidateIds.length) {
      return null;
    }


    // --------------------------------------------------
    // Vérification :
    // la personne doit être un humain Q5.
    // --------------------------------------------------

    const values =
      candidateIds
        .map(
          (id) =>
            `wd:${id}`
        )
        .join(" ");


    const query = `
      PREFIX wd:
        <http://www.wikidata.org/entity/>

      PREFIX wdt:
        <http://www.wikidata.org/prop/direct/>

      SELECT DISTINCT ?person
      WHERE {

        VALUES ?person {
          ${values}
        }

        ?person
          wdt:P31
          wd:Q5.
      }
    `;


    const validation =
      await fetchSparql(
        query,
        signal
      );


    const validIds =
      new Set(
        (
          validation
            ?.results
            ?.bindings ||
          []
        )
          .map(
            (binding) =>
              getEntityIdFromUrl(
                binding
                  ?.person
                  ?.value
              )
          )
          .filter(Boolean)
      );


    return (
      candidateIds.find(
        (id) =>
          validIds.has(id)
      ) ||
      null
    );
  };


// ======================================================
// WIKIDATA — RÉSOLUTION FINALE
// ======================================================

const resolveWikidataPersonId =
  async ({
    actorId,
    actorName,
    languageKey,
    signal,
  }) => {

    // --------------------------------------------------
    // 1. TMDB ID
    // --------------------------------------------------

    try {
      const fromTmdb =
        await findPersonFromTmdbId(
          actorId,
          signal
        );

      if (fromTmdb) {
        return fromTmdb;
      }
    } catch (error) {
      if (
        error?.name ===
        "AbortError"
      ) {
        throw error;
      }

      console.warn(
        "MovieFrenzy — recherche Wikidata TMDB échouée :",
        error
      );
    }


    // --------------------------------------------------
    // 2. NOM
    // --------------------------------------------------

    return searchWikidataByName({
      actorName,
      languageKey,
      signal,
    });
  };


// ======================================================
// WIKIDATA — LANGUES
// ======================================================

const getWikidataLanguages = (
  languageKey
) =>
  getLanguagePriority(
    languageKey
  )
    .join(",");


// ======================================================
// WIKIDATA — SITELINK WIKIPÉDIA
// ======================================================
//
// Si on connaît déjà le Q-ID,
// inutile de rechercher aveuglément l'acteur sur Wikipédia.
//
// On demande directement ses sitelinks.
// ======================================================

const fetchWikipediaSitelinksFromWikidata =
  async ({
    wikidataId,
    signal,
  }) => {

    if (
      !/^Q\d+$/.test(
        wikidataId || ""
      )
    ) {
      return {};
    }


    const params =
      new URLSearchParams({
        action:
          "wbgetentities",

        ids:
          wikidataId,

        props:
          "sitelinks",

        sitefilter:
          "frwiki|enwiki|eswiki|dewiki",

        format:
          "json",

        origin:
          "*",
      });


    const data =
      await fetchJson(
        `${WIKIDATA_API_ENDPOINT}?${params.toString()}`,
        {
          signal,
        }
      );


    const sitelinks =
      data
        ?.entities
        ?.[wikidataId]
        ?.sitelinks ||
      {};


    return {
      fr:
        sitelinks
          ?.frwiki
          ?.title ||
        null,

      en:
        sitelinks
          ?.enwiki
          ?.title ||
        null,

      es:
        sitelinks
          ?.eswiki
          ?.title ||
        null,

      de:
        sitelinks
          ?.dewiki
          ?.title ||
        null,
    };
  };


// ======================================================
// WIKIDATA — PALMARÈS
// ======================================================
//
// P166  = award received
// P1411 = nominated for
//
// Qualifiers :
//
// P585  = date / point in time
// P1686 = for work
//
// ======================================================

const fetchWikidataPalmares =
  async ({
    wikidataId,
    languageKey,
    signal,
  }) => {

    if (
      !/^Q\d+$/.test(
        wikidataId || ""
      )
    ) {
      return {
        wins: [],
        nominations: [],
      };
    }


    const languages =
      getWikidataLanguages(
        languageKey
      );


    const query = `
      PREFIX wd:
        <http://www.wikidata.org/entity/>

      PREFIX wdt:
        <http://www.wikidata.org/prop/direct/>

      PREFIX p:
        <http://www.wikidata.org/prop/>

      PREFIX ps:
        <http://www.wikidata.org/prop/statement/>

      PREFIX pq:
        <http://www.wikidata.org/prop/qualifier/>

      PREFIX wikibase:
        <http://wikiba.se/ontology#>

      PREFIX bd:
        <http://www.bigdata.com/rdf#>


      SELECT DISTINCT
        ?resultType
        ?award
        ?awardLabel
        ?awardGroup
        ?awardGroupLabel
        ?date
        ?work
        ?workLabel

      WHERE {

        VALUES ?person {
          wd:${wikidataId}
        }


        {
          BIND(
            "win"
            AS ?resultType
          )

          ?person
            p:P166
            ?statement.

          ?statement
            ps:P166
            ?award.
        }


        UNION


        {
          BIND(
            "nomination"
            AS ?resultType
          )

          ?person
            p:P1411
            ?statement.

          ?statement
            ps:P1411
            ?award.
        }


        OPTIONAL {

          ?statement
            pq:P585
            ?date.

        }


        OPTIONAL {

          ?statement
            pq:P1686
            ?work.

        }


        OPTIONAL {

          ?award
            wdt:P361
            ?awardGroup.

        }


        SERVICE wikibase:label {

          bd:serviceParam
            wikibase:language
            "${languages}".

        }

      }

      ORDER BY
        DESC(?date)
        ?awardLabel

      LIMIT 400
    `;


    const data =
      await fetchSparql(
        query,
        signal
      );


    const bindings =
      data
        ?.results
        ?.bindings ||
      [];


    const entries =
      bindings
        .map(
          (binding) => {

            const resultType =
              binding
                ?.resultType
                ?.value;


            const awardLabel =
              cleanText(
                binding
                  ?.awardLabel
                  ?.value
              );


            const awardGroupLabel =
              cleanText(
                binding
                  ?.awardGroupLabel
                  ?.value
              );


            const workLabel =
              cleanText(
                binding
                  ?.workLabel
                  ?.value
              );


            const dateValue =
              binding
                ?.date
                ?.value ||
              "";


            if (!awardLabel) {
              return null;
            }


            // ------------------------------------------
            // FILTRAGE CINÉMA
            // ------------------------------------------

            if (
              !isCinemaAward({
                awardLabel,
                awardGroupLabel,
                relatedWorkLabel:
                  workLabel,
              })
            ) {
              return null;
            }


            // ------------------------------------------
            // ANNÉE
            // ------------------------------------------

            let year =
              null;


            const dateMatch =
              String(
                dateValue
              )
                .match(
                  /^(\d{4})-/
                );


            if (dateMatch) {
              year =
                dateMatch[1];
            }


            if (!year) {

              const labelYear =
                `${awardGroupLabel} ${awardLabel}`
                  .match(
                    /\b(19|20)\d{2}\b/
                  );

              if (labelYear) {
                year =
                  labelYear[0];
              }
            }


            // ------------------------------------------
            // PRIX / CATÉGORIE
            // ------------------------------------------

            let award =
              awardLabel;

            let category =
              null;


            if (
              awardGroupLabel &&
              normalizeText(
                awardGroupLabel
              ) !==
              normalizeText(
                awardLabel
              )
            ) {
              award =
                awardGroupLabel;

              category =
                awardLabel;
            }


            return {

              id:
                [
                  "wikidata",
                  resultType,
                  binding
                    ?.award
                    ?.value,
                  dateValue,
                  binding
                    ?.work
                    ?.value,
                ]
                  .filter(Boolean)
                  .join("|"),

              source:
                "wikidata",

              resultType,

              award,

              category,

              year,

              movie:
                workLabel ||
                null,

              awardEntityId:
                getEntityIdFromUrl(
                  binding
                    ?.award
                    ?.value
                ),
            };
          }
        )
        .filter(Boolean);


    return {
      wins:
        entries.filter(
          (item) =>
            item.resultType ===
            "win"
        ),

      nominations:
        entries.filter(
          (item) =>
            item.resultType ===
            "nomination"
        ),
    };
  };


// ======================================================
// WIKIPÉDIA — API
// ======================================================

const fetchWikipediaApi =
  async ({
    language,
    params,
    signal,
  }) => {

    const endpoint =
      WIKIPEDIA_API_ENDPOINTS[
      language
      ] ||
      WIKIPEDIA_API_ENDPOINTS.en;


    const query =
      new URLSearchParams({
        ...params,
        format:
          "json",
        origin:
          "*",
      });


    return fetchJson(
      `${endpoint}?${query.toString()}`,
      {
        signal,
      }
    );
  };


// ======================================================
// WIKIPÉDIA — TERMES DE SECTIONS
// ======================================================

const WIKIPEDIA_AWARD_SECTION_TERMS = [

  // FR
  "distinctions",
  "distinction",

  "recompenses",
  "récompenses",

  "recompense",
  "récompense",

  "prix",

  "palmares",
  "palmarès",

  "nominations",
  "nomination",

  "honneurs",
  "honneur",

  // EN
  "awards",
  "award",

  "accolades",
  "accolade",

  "honours",
  "honors",

  "honour",
  "honor",

  "nominations",

  // ES
  "premios",
  "premio",

  "nominaciones",
  "nominación",

  "galardones",
  "galardón",

  "honores",

  // DE
  "auszeichnungen",
  "auszeichnung",

  "preise",
  "preis",

  "nominierungen",
  "nominierung",

  "ehrungen",
  "ehrung",
];


// ======================================================
// WIKIPÉDIA — NOMINATIONS
// ======================================================

const WIKIPEDIA_NOMINATION_TERMS = [
  "nomination",
  "nominations",

  "nomine",
  "nominé",
  "nominée",

  "nominee",
  "nominated",

  "nominado",
  "nominada",

  "nominacion",
  "nominación",

  "nominierung",
  "nominiert",

  "finaliste",
  "finalist",
];


// ======================================================
// WIKIPÉDIA — TROUVER LA PAGE
// ======================================================

const searchWikipediaActorPage =
  async ({
    actorName,
    language,
    signal,
  }) => {

    const name =
      cleanText(
        actorName
      );

    if (!name) {
      return null;
    }


    const data =
      await fetchWikipediaApi({
        language,
        signal,

        params: {
          action:
            "query",

          list:
            "search",

          srsearch:
            `"${name}"`,

          srnamespace:
            "0",

          srlimit:
            "8",
        },
      });


    const results =
      Array.isArray(
        data
          ?.query
          ?.search
      )
        ? data.query.search
        : [];


    if (!results.length) {
      return null;
    }


    const normalizedName =
      normalizeText(
        name
      );


    // --------------------------------------------------
    // 1. Titre exact
    // --------------------------------------------------

    const exact =
      results.find(
        (item) =>
          normalizeText(
            item?.title
          ) ===
          normalizedName
      );


    if (exact?.title) {
      return exact.title;
    }


    // --------------------------------------------------
    // 2. "Nom (acteur)"
    // --------------------------------------------------

    const qualified =
      results.find(
        (item) => {

          const title =
            normalizeText(
              item?.title
            );

          return (
            title.startsWith(
              `${normalizedName} (`
            ) ||
            title.startsWith(
              `${normalizedName},`
            )
          );
        }
      );


    return (
      qualified?.title ||
      null
    );
  };


// ======================================================
// WIKIPÉDIA — SECTIONS
// ======================================================

const getWikipediaAwardSections =
  async ({
    pageTitle,
    language,
    signal,
  }) => {

    const data =
      await fetchWikipediaApi({
        language,
        signal,

        params: {
          action:
            "parse",

          page:
            pageTitle,

          prop:
            "sections",

          redirects:
            "1",
        },
      });


    const sections =
      Array.isArray(
        data
          ?.parse
          ?.sections
      )
        ? data.parse.sections
        : [];


    return sections.filter(
      (section) => {

        const line =
          normalizeText(
            section?.line
          );


        return (
          WIKIPEDIA_AWARD_SECTION_TERMS
            .some(
              (term) =>
                line.includes(
                  normalizeText(
                    term
                  )
                )
            )
        );
      }
    );
  };


// ======================================================
// WIKIPÉDIA — TYPE DE RÉSULTAT
// ======================================================

const detectWikipediaResultType = ({
  text,
  sectionTitle,
}) => {

  const combined =
    normalizeText(
      `${sectionTitle} ${text}`
    );


  const isNomination =
    WIKIPEDIA_NOMINATION_TERMS
      .some(
        (term) =>
          combined.includes(
            normalizeText(
              term
            )
          )
      );


  return isNomination
    ? "nomination"
    : "win";
};


// ======================================================
// WIKIPÉDIA — EXTRACTION DU FILM
// ======================================================

const extractWikipediaMovie = (
  text
) => {

  const source =
    cleanText(
      text
    );


  // ----------------------------------------------------
  // Formes fréquentes :
  //
  // "... pour Incendies"
  // "... for The Godfather"
  //
  // ----------------------------------------------------

  const match =
    source.match(
      /\b(?:pour|for|por|für)\s+(?:le film\s+|the film\s+)?[«“"]?([^.;|]{2,120}?)[»”"]?(?=$|[.;|])/i
    );


  if (
    match?.[1]
  ) {
    return cleanText(
      match[1]
    );
  }


  return null;
};


// ======================================================
// WIKIPÉDIA — CRÉER UNE ENTRÉE
// ======================================================

const buildWikipediaEntry = ({
  text,
  sectionTitle,
  index,
}) => {

  let body =
    cleanText(
      text
    );


  if (
    body.length < 4 ||
    body.length > 500
  ) {
    return null;
  }


  const combined =
    `${sectionTitle} ${body}`;


  // ----------------------------------------------------
  // EXCLUSION DU NON-CINÉMA
  // ----------------------------------------------------

  if (
    !isCinemaAward({
      awardLabel:
        combined,

      awardGroupLabel:
        sectionTitle,

      relatedWorkLabel:
        "",
    })
  ) {
    return null;
  }


  const resultType =
    detectWikipediaResultType({
      text:
        body,

      sectionTitle,
    });


  // ----------------------------------------------------
  // ANNÉE
  // ----------------------------------------------------

  const yearMatch =
    body.match(
      /\b(19|20)\d{2}\b/
    );


  const year =
    yearMatch
      ? yearMatch[0]
      : null;


  if (year) {

    body =
      body
        .replace(
          year,
          ""
        )
        .replace(
          /^[\s:;,.–—-]+/,
          ""
        )
        .trim();
  }


  // ----------------------------------------------------
  // FILM
  // ----------------------------------------------------

  const movie =
    extractWikipediaMovie(
      body
    );


  // ----------------------------------------------------
  // PRIX
  // ----------------------------------------------------

  let award =
    body;


  const normalizedSection =
    normalizeText(
      sectionTitle
    );


  const sectionIsGeneric =
    WIKIPEDIA_AWARD_SECTION_TERMS
      .some(
        (term) =>
          normalizedSection ===
          normalizeText(
            term
          )
      );


  // ----------------------------------------------------
  // Exemple :
  //
  // Section : Prix Iris
  // Ligne   : meilleur acteur
  //
  // =>
  //
  // Prix Iris — meilleur acteur
  // ----------------------------------------------------

  if (
    sectionTitle &&
    !sectionIsGeneric &&
    !normalizeText(
      body
    ).includes(
      normalizeText(
        sectionTitle
      )
    )
  ) {
    award =
      `${sectionTitle} — ${body}`;
  }


  award =
    cleanText(
      award
    );


  if (!award) {
    return null;
  }


  return {

    id:
      [
        "wikipedia",
        resultType,
        normalizeText(
          award
        ),
        year || "",
        normalizeText(
          movie
        ),
        index,
      ]
        .join("|"),

    source:
      "wikipedia",

    resultType,

    award,

    category:
      null,

    year,

    movie,

    awardEntityId:
      null,
  };
};


// ======================================================
// WIKIPÉDIA — PARSER UNE SECTION
// ======================================================

const parseWikipediaAwardSection = ({
  html,
  sectionTitle,
}) => {

  const documentNode =
    new DOMParser()
      .parseFromString(
        html || "",
        "text/html"
      );


  // ----------------------------------------------------
  // VIRER LES PARASITES
  // ----------------------------------------------------

  documentNode
    .querySelectorAll(
      [
        "sup",
        ".reference",
        ".mw-editsection",
        "style",
        "script",
        "noscript",
      ].join(",")
    )
    .forEach(
      (node) =>
        node.remove()
    );


  const rows =
    [];


  // ====================================================
  // LISTES
  // ====================================================

  documentNode
    .querySelectorAll(
      "li"
    )
    .forEach(
      (node) => {

        // Éviter qu'un LI parent reprenne aussi
        // tout le texte de ses sous-LI.
        if (
          node.querySelector(
            ":scope > ul, :scope > ol"
          )
        ) {
          return;
        }


        const text =
          cleanText(
            node.textContent
          );


        if (text) {
          rows.push(
            text
          );
        }
      }
    );


  // ====================================================
  // TABLEAUX
  // ====================================================

  documentNode
    .querySelectorAll(
      "table tr"
    )
    .forEach(
      (row) => {

        const cells =
          Array.from(
            row.querySelectorAll(
              ":scope > th, :scope > td"
            )
          )
            .map(
              (cell) =>
                cleanText(
                  cell.textContent
                )
            )
            .filter(Boolean);


        if (
          cells.length >= 2
        ) {
          rows.push(
            cells.join(
              " — "
            )
          );
        }
      }
    );


  // ====================================================
  // PARAGRAPHES COURTS
  // ====================================================

  documentNode
    .querySelectorAll(
      "p"
    )
    .forEach(
      (node) => {

        const text =
          cleanText(
            node.textContent
          );


        if (
          text &&
          text.length >= 5 &&
          text.length <= 300
        ) {
          rows.push(
            text
          );
        }
      }
    );


  const uniqueRows =
    Array.from(
      new Set(
        rows
      )
    );


  return uniqueRows
    .map(
      (
        text,
        index
      ) =>
        buildWikipediaEntry({
          text,
          sectionTitle,
          index,
        })
    )
    .filter(Boolean);
};


// ======================================================
// WIKIPÉDIA — CHARGER UNE PAGE
// ======================================================

const fetchWikipediaPalmaresForPage =
  async ({
    pageTitle,
    language,
    signal,
  }) => {

    if (!pageTitle) {
      return [];
    }


    const sections =
      await getWikipediaAwardSections({
        pageTitle,
        language,
        signal,
      });


    if (!sections.length) {
      return [];
    }


    // --------------------------------------------------
    // IMPORTANT :
    //
    // ANCIENNEMENT :
    // les sections étaient chargées l'une après l'autre.
    //
    // MAINTENANT :
    // elles partent EN PARALLÈLE.
    // --------------------------------------------------

    const sectionPromises =
      sections
        .slice(
          0,
          10
        )
        .map(
          async (
            section
          ) => {

            const data =
              await fetchWikipediaApi({
                language,
                signal,

                params: {
                  action:
                    "parse",

                  page:
                    pageTitle,

                  section:
                    String(
                      section.index
                    ),

                  prop:
                    "text",

                  redirects:
                    "1",
                },
              });


            const html =
              data
                ?.parse
                ?.text
              ?.["*"] ||
              "";


            return parseWikipediaAwardSection({
              html,

              sectionTitle:
                section.line ||
                "",
            });
          }
        );


    const settled =
      await Promise.allSettled(
        sectionPromises
      );


    return settled
      .flatMap(
        (result) =>

          result.status ===
            "fulfilled"

            ? result.value

            : []
      );
  };


// ======================================================
// WIKIPÉDIA — RÉSOLUTION
// ======================================================

const fetchWikipediaPalmares =
  async ({
    actorName,
    wikidataId,
    languageKey,
    signal,
  }) => {

    const languages =
      getLanguagePriority(
        languageKey
      );


    // --------------------------------------------------
    // SI WIKIDATA CONNAÎT LA PERSONNE :
    //
    // on récupère ses vrais sitelinks Wikipédia.
    //
    // C'est beaucoup plus sûr qu'une recherche par nom.
    // --------------------------------------------------

    let sitelinks =
      {};


    if (wikidataId) {

      try {

        sitelinks =
          await fetchWikipediaSitelinksFromWikidata({
            wikidataId,
            signal,
          });

      } catch (error) {

        if (
          error?.name ===
          "AbortError"
        ) {
          throw error;
        }

        console.warn(
          "MovieFrenzy — sitelinks Wikipédia indisponibles :",
          error
        );
      }
    }


    // --------------------------------------------------
    // On teste les langues dans l'ordre :
    //
    // langue UI
    // puis FR
    // puis EN
    // puis ES
    // puis DE
    //
    // IMPORTANT :
    //
    // Dès qu'une page donne de vraies données,
    // on garde cette page.
    // --------------------------------------------------

    for (
      const language
      of languages
    ) {

      let pageTitle =
        sitelinks[
        language
        ] ||
        null;


      if (!pageTitle) {

        try {

          pageTitle =
            await searchWikipediaActorPage({
              actorName,
              language,
              signal,
            });

        } catch (error) {

          if (
            error?.name ===
            "AbortError"
          ) {
            throw error;
          }

          continue;
        }
      }


      if (!pageTitle) {
        continue;
      }


      try {

        const entries =
          await fetchWikipediaPalmaresForPage({
            pageTitle,
            language,
            signal,
          });


        if (entries.length) {

          return {
            entries,
            language,
            pageTitle,
          };
        }

      } catch (error) {

        if (
          error?.name ===
          "AbortError"
        ) {
          throw error;
        }

        console.warn(
          `MovieFrenzy — palmarès Wikipédia ${language} indisponible :`,
          error
        );
      }
    }


    return {
      entries: [],
      language: null,
      pageTitle: null,
    };
  };


// ======================================================
// QUALITÉ D'UNE ENTRÉE
// ======================================================
//
// Lorsqu'une même récompense existe dans les deux sources,
// on préfère l'entrée qui contient le plus d'informations.
//
// Exemple :
//
// Wikidata :
// "Prix Jutra du meilleur acteur"
//
// Wikipédia :
// "Prix Jutra du meilleur acteur"
// + 2008
// + J'ai serré la main du diable
//
// => Wikipédia gagne.
//
// ======================================================

const getEntryRichness = (
  item
) => {

  let score =
    0;

  if (item?.award) {
    score += 4;
  }

  if (item?.category) {
    score += 2;
  }

  if (item?.year) {
    score += 2;
  }

  if (item?.movie) {
    score += 3;
  }

  if (
    item?.source ===
    "wikidata"
  ) {
    score += 1;
  }

  return score;
};


// ======================================================
// CLÉ DE DÉDOUBLONNAGE
// ======================================================

const simplifyAwardForKey = (
  value = ""
) =>
  normalizeText(
    value
  )
    .replace(
      /\b(prix|award|awards|premio|premios)\b/g,
      " "
    )
    .replace(
      /\s+/g,
      " "
    )
    .trim();


const createEntryKey = (
  item
) => {

  const award =
    simplifyAwardForKey(
      item?.award
    );

  const category =
    simplifyAwardForKey(
      item?.category
    );

  const year =
    String(
      item?.year ||
      ""
    );

  const movie =
    normalizeText(
      item?.movie
    );


  return [
    item?.resultType ||
    "",

    award,

    category,

    year,

    movie,
  ]
    .join("|");
};


// ======================================================
// DÉDOUBLONNAGE
// ======================================================

const dedupeEntries = (
  items
) => {

  const map =
    new Map();


  items.forEach(
    (item) => {

      if (!item?.award) {
        return;
      }


      const key =
        createEntryKey(
          item
        );


      if (
        !map.has(
          key
        )
      ) {
        map.set(
          key,
          item
        );

        return;
      }


      const current =
        map.get(
          key
        );


      if (
        getEntryRichness(
          item
        ) >
        getEntryRichness(
          current
        )
      ) {
        map.set(
          key,
          item
        );
      }
    }
  );


  return Array.from(
    map.values()
  );
};


// ======================================================
// SUPPRESSION NOMINATION SI PRIX REMPORTÉ IDENTIQUE
// ======================================================

const removeWonAwardsFromNominations = ({
  wins,
  nominations,
}) => {

  const winKeys =
    new Set(
      wins.map(
        (item) => {

          return [
            simplifyAwardForKey(
              item?.award
            ),

            simplifyAwardForKey(
              item?.category
            ),

            String(
              item?.year ||
              ""
            ),

            normalizeText(
              item?.movie
            ),
          ]
            .join("|");
        }
      )
    );


  return nominations.filter(
    (item) => {

      const key =
        [
          simplifyAwardForKey(
            item?.award
          ),

          simplifyAwardForKey(
            item?.category
          ),

          String(
            item?.year ||
            ""
          ),

          normalizeText(
            item?.movie
          ),
        ]
          .join("|");


      return !winKeys.has(
        key
      );
    }
  );
};


// ======================================================
// TRI
// ======================================================

const sortEntries = (
  items,
  languageKey
) =>
  [...items]
    .sort(
      (
        a,
        b
      ) => {

        const yearA =
          Number(
            a?.year ||
            0
          );

        const yearB =
          Number(
            b?.year ||
            0
          );


        if (
          yearA !==
          yearB
        ) {
          return (
            yearB -
            yearA
          );
        }


        return String(
          a?.award ||
          ""
        )
          .localeCompare(
            String(
              b?.award ||
              ""
            ),

            languageKey
          );
      }
    );


// ======================================================
// FUSION WIKIDATA + WIKIPÉDIA
// ======================================================

const mergePalmaresSources = ({
  wikidataResult,
  wikipediaResult,
  languageKey,
}) => {

  const wikipediaEntries =
    wikipediaResult
      ?.entries ||
    [];


  const wikiWins =
    wikipediaEntries.filter(
      (item) =>
        item.resultType ===
        "win"
    );


  const wikiNominations =
    wikipediaEntries.filter(
      (item) =>
        item.resultType ===
        "nomination"
    );


  let wins =
    dedupeEntries([
      ...(
        wikidataResult
          ?.wins ||
        []
      ),

      ...wikiWins,
    ]);


  let nominations =
    dedupeEntries([
      ...(
        wikidataResult
          ?.nominations ||
        []
      ),

      ...wikiNominations,
    ]);


  nominations =
    removeWonAwardsFromNominations({
      wins,
      nominations,
    });


  wins =
    sortEntries(
      wins,
      languageKey
    );


  nominations =
    sortEntries(
      nominations,
      languageKey
    );


  return {
    wins,
    nominations,
  };
};


// ======================================================
// DÉTERMINER LES SOURCES UTILISÉES
// ======================================================

const determineSourceType = ({
  wikidataResult,
  wikipediaResult,
}) => {

  const wikidataCount =
    (
      wikidataResult
        ?.wins
        ?.length ||
      0
    ) +
    (
      wikidataResult
        ?.nominations
        ?.length ||
      0
    );


  const wikipediaCount =
    wikipediaResult
      ?.entries
      ?.length ||
    0;


  if (
    wikidataCount > 0 &&
    wikipediaCount > 0
  ) {
    return "both";
  }


  if (
    wikidataCount > 0
  ) {
    return "wikidata";
  }


  if (
    wikipediaCount > 0
  ) {
    return "wikipedia";
  }


  return null;
};


// ======================================================
// CACHE KEY
// ======================================================

const createCacheKey = ({
  actorId,
  actorName,
  languageKey,
}) =>
  [
    String(
      actorId ||
      ""
    ),

    normalizeText(
      actorName
    ),

    languageKey,
  ]
    .join("|");


// ======================================================
// COMPOSANT
// ======================================================

const ActorPalmaresPopover = ({
  actorId,
  actorName,
}) => {

  const {
    currentLanguage,
  } = useLanguage();


  const languageKey =
    normalizeLanguageKey(
      currentLanguage
    );


  const copy =
    PALMARES_COPY[
    languageKey
    ] ||
    PALMARES_COPY.en;


  const popoverRef =
    useRef(
      null
    );


  const [
    isPopoverOpen,
    setIsPopoverOpen,
  ] = useState(
    false
  );


  const [
    status,
    setStatus,
  ] = useState(
    "loading"
  );


  const [
    palmares,
    setPalmares,
  ] = useState({
    wins: [],
    nominations: [],
  });


  const [
    sourceType,
    setSourceType,
  ] = useState(
    null
  );


  const [
    wikidataId,
    setWikidataId,
  ] = useState(
    null
  );


  const popoverId =
    useMemo(
      () =>
        `actor-palmares-${actorId ||
        normalizeText(
          actorName
        )
          .replace(
            /\s+/g,
            "-"
          ) ||
        "person"
        }`,

      [
        actorId,
        actorName,
      ]
    );


  const cacheKey =
    useMemo(
      () =>
        createCacheKey({
          actorId,
          actorName,
          languageKey,
        }),

      [
        actorId,
        actorName,
        languageKey,
      ]
    );


  // ====================================================
  // CHARGEMENT
  // ====================================================

  useEffect(
    () => {

      if (!actorName) {

        setStatus(
          "empty"
        );

        setPalmares({
          wins: [],
          nominations: [],
        });

        setSourceType(
          null
        );

        return undefined;
      }


      // -------------------------------------------------
      // CACHE
      // -------------------------------------------------

      const cached =
        PALMARES_CACHE.get(
          cacheKey
        );


      if (cached) {

        setPalmares(
          cached.palmares
        );

        setSourceType(
          cached.sourceType
        );

        setWikidataId(
          cached.wikidataId ||
          null
        );

        setStatus(
          cached.status
        );

        return undefined;
      }


      // -------------------------------------------------
      // NOUVEAU CHARGEMENT
      // -------------------------------------------------

      const controller =
        new AbortController();


      let isCurrent =
        true;


      const loadPalmares =
        async () => {

          setStatus(
            "loading"
          );

          setPalmares({
            wins: [],
            nominations: [],
          });

          setSourceType(
            null
          );

          setWikidataId(
            null
          );


          try {

            // ==========================================
            // ÉTAPE 1
            //
            // Trouver la personne Wikidata.
            // ==========================================

            let personId =
              null;


            try {

              personId =
                await resolveWikidataPersonId({
                  actorId,
                  actorName,
                  languageKey,

                  signal:
                    controller.signal,
                });

            } catch (error) {

              if (
                error?.name ===
                "AbortError"
              ) {
                throw error;
              }

              console.warn(
                "MovieFrenzy — identification Wikidata impossible :",
                error
              );
            }


            if (
              !isCurrent
            ) {
              return;
            }


            setWikidataId(
              personId ||
              null
            );


            // ==========================================
            // ÉTAPE 2
            //
            // WIKIDATA + WIKIPÉDIA EN PARALLÈLE.
            //
            // Très important :
            //
            // Wikipédia n'est PLUS un simple fallback.
            //
            // On utilise les DEUX.
            // ==========================================

            const wikidataPromise =
              personId

                ? fetchWikidataPalmares({
                  wikidataId:
                    personId,

                  languageKey,

                  signal:
                    controller.signal,
                })

                : Promise.resolve({
                  wins: [],
                  nominations: [],
                });


            const wikipediaPromise =
              fetchWikipediaPalmares({
                actorName,

                wikidataId:
                  personId,

                languageKey,

                signal:
                  controller.signal,
              });


            const [
              wikidataSettled,
              wikipediaSettled,
            ] =
              await Promise.allSettled([
                wikidataPromise,
                wikipediaPromise,
              ]);


            if (
              !isCurrent
            ) {
              return;
            }


            // ==========================================
            // RÉCUPÉRATION DES DEUX SOURCES
            // ==========================================

            const wikidataResult =
              wikidataSettled.status ===
                "fulfilled"

                ? wikidataSettled.value

                : {
                  wins: [],
                  nominations: [],
                };


            const wikipediaResult =
              wikipediaSettled.status ===
                "fulfilled"

                ? wikipediaSettled.value

                : {
                  entries: [],
                  language: null,
                  pageTitle: null,
                };


            // ==========================================
            // FUSION
            // ==========================================

            const merged =
              mergePalmaresSources({
                wikidataResult,
                wikipediaResult,
                languageKey,
              });


            const resolvedSourceType =
              determineSourceType({
                wikidataResult,
                wikipediaResult,
              });


            const total =
              merged.wins.length +
              merged.nominations.length;


            const finalStatus =
              total > 0
                ? "ready"
                : "empty";


            const cacheValue = {
              palmares:
                merged,

              sourceType:
                resolvedSourceType,

              wikidataId:
                personId,

              status:
                finalStatus,
            };


            // ==========================================
            // CACHE
            // ==========================================

            PALMARES_CACHE.set(
              cacheKey,
              cacheValue
            );


            // ==========================================
            // STATE
            // ==========================================

            setPalmares(
              merged
            );

            setSourceType(
              resolvedSourceType
            );

            setStatus(
              finalStatus
            );


            // ==========================================
            // DEBUG
            // ==========================================

            if (
              process.env
                .NODE_ENV !==
              "production"
            ) {

              console.info(
                "🏆 MovieFrenzy Palmarès cinéma :",
                {
                  actorId,
                  actorName,

                  wikidataId:
                    personId,

                  source:
                    resolvedSourceType,

                  wikipediaLanguage:
                    wikipediaResult
                      ?.language,

                  wikipediaPage:
                    wikipediaResult
                      ?.pageTitle,

                  wins:
                    merged.wins,

                  nominations:
                    merged.nominations,
                }
              );
            }

          } catch (error) {

            if (
              error?.name ===
              "AbortError"
            ) {
              return;
            }


            console.warn(
              "MovieFrenzy — impossible de récupérer le palmarès cinéma :",
              {
                actorId,
                actorName,
                error,
              }
            );


            if (
              !isCurrent
            ) {
              return;
            }


            setPalmares({
              wins: [],
              nominations: [],
            });

            setSourceType(
              null
            );

            setStatus(
              "error"
            );
          }
        };


      loadPalmares();


      return () => {

        isCurrent =
          false;

        controller.abort();
      };
    },

    [
      actorId,
      actorName,
      languageKey,
      cacheKey,
    ]
  );


  // ====================================================
  // SYNCHRONISATION POPOVER API
  // ====================================================

  useEffect(
    () => {

      const popover =
        popoverRef.current;


      if (!popover) {
        return undefined;
      }


      const handleToggle =
        (event) => {

          setIsPopoverOpen(
            event.newState ===
            "open"
          );
        };


      popover.addEventListener(
        "toggle",
        handleToggle
      );


      return () => {

        popover.removeEventListener(
          "toggle",
          handleToggle
        );
      };
    },

    []
  );


  // ====================================================
  // OUVRIR
  // ====================================================

  const openPopover =
    () => {

      const popover =
        popoverRef.current;


      if (
        popover &&
        typeof popover
          .showPopover ===
        "function"
      ) {

        popover.showPopover();
      }
    };


  // ====================================================
  // FERMER
  // ====================================================

  const closePopover =
    () => {

      const popover =
        popoverRef.current;


      if (
        popover &&
        typeof popover
          .hidePopover ===
        "function"
      ) {

        popover.hidePopover();
      }
    };


  // ====================================================
  // DONNÉES
  // ====================================================

  const wins =
    palmares.wins ||
    [];


  const nominations =
    palmares.nominations ||
    [];


  // ====================================================
  // SOURCE
  // ====================================================

  const sourceLabel =
    sourceType ===
      "both"

      ? copy.sourceBoth

      : sourceType ===
        "wikipedia"

        ? copy.sourceWikipedia

        : sourceType ===
          "wikidata"

          ? copy.sourceWikidata

          : null;


  // ====================================================
  // CARTE
  // ====================================================

  const renderEntry = (
    item,
    type
  ) => (

    <article
      key={
        item.id
      }

      className={`
        actor-palmares-entry
        actor-palmares-entry--${type}
      `}
    >

      <div className="actor-palmares-entry__top">

        <strong>
          {item.award}
        </strong>


        {item.year && (

          <span className="actor-palmares-entry__year">

            {item.year}

          </span>

        )}

      </div>


      {item.category && (

        <p className="actor-palmares-entry__category">

          {item.category}

        </p>

      )}


      {item.movie && (

        <p className="actor-palmares-entry__movie">

          <span
            aria-hidden="true"
          >
            🎬
          </span>


          <span>

            <small>
              {copy.movie}
            </small>


            <strong>
              {item.movie}
            </strong>

          </span>

        </p>

      )}

    </article>
  );


  // ====================================================
  // RENDER
  //
  // IMPORTANT :
  //
  // PLUS DE :
  //
  // if (status !== "ready") return null;
  //
  // Le bouton existe IMMÉDIATEMENT.
  // ====================================================

  return (
    <>

      {/* =================================================
          BOUTON
      ================================================= */}

      <button
        type="button"

        className="actor-palmares-button"

        aria-controls={
          popoverId
        }

        aria-expanded={
          isPopoverOpen
        }

        aria-haspopup="dialog"

        onClick={
          openPopover
        }
      >

        <span
          className="actor-palmares-button__icon"
          aria-hidden="true"
        >
          🏆
        </span>


        <span className="actor-palmares-button__label">

          {copy.trigger}

        </span>

      </button>


      {/* =================================================
          POPOVER
      ================================================= */}

      <div
        ref={
          popoverRef
        }

        id={
          popoverId
        }

        className="actor-palmares-popover"

        popover="auto"

        role="dialog"

        aria-labelledby={
          `${popoverId}-title`
        }
      >

        {/* ===============================================
            EN-TÊTE
        =============================================== */}

        <header className="actor-palmares-popover__header">

          <div>

            <span className="actor-palmares-popover__eyebrow">

              🏆 MovieFrenzy

            </span>


            <h2
              id={
                `${popoverId}-title`
              }
            >

              {copy.title}

            </h2>


            <p className="actor-palmares-popover__actor">

              {actorName}

            </p>

          </div>


          <button
            type="button"

            className="actor-palmares-popover__close"

            onClick={
              closePopover
            }

            aria-label={
              copy.close
            }
          >
            ×
          </button>

        </header>


        {/* ===============================================
            CHARGEMENT
        =============================================== */}

        {status ===
          "loading" && (

            <section className="actor-palmares-section">

              <article className="actor-palmares-entry">

                <strong>
                  {copy.loading}
                </strong>

              </article>

            </section>

          )}


        {/* ===============================================
            ERREUR
        =============================================== */}

        {status ===
          "error" && (

            <section className="actor-palmares-section">

              <article className="actor-palmares-entry">

                <strong>
                  {copy.error}
                </strong>

              </article>

            </section>

          )}


        {/* ===============================================
            AUCUNE DONNÉE
        =============================================== */}

        {status ===
          "empty" && (

            <section className="actor-palmares-section">

              <article className="actor-palmares-entry">

                <strong>
                  {copy.empty}
                </strong>

              </article>

            </section>

          )}


        {/* ===============================================
            DONNÉES DISPONIBLES
        =============================================== */}

        {status ===
          "ready" && (

            <>

              {/* ===========================================
                RÉSUMÉ
            =========================================== */}

              <div className="actor-palmares-summary">

                <article className="actor-palmares-summary__item">

                  <span
                    aria-hidden="true"
                  >
                    🏆
                  </span>


                  <strong>
                    {wins.length}
                  </strong>


                  <small>
                    {copy.wins}
                  </small>

                </article>


                <article className="actor-palmares-summary__item">

                  <span
                    aria-hidden="true"
                  >
                    🎖️
                  </span>


                  <strong>
                    {nominations.length}
                  </strong>


                  <small>
                    {copy.nominations}
                  </small>

                </article>

              </div>


              {/* ===========================================
                PRIX REMPORTÉS
            =========================================== */}

              {wins.length >
                0 && (

                  <section className="actor-palmares-section">

                    <h3>

                      <span
                        aria-hidden="true"
                      >
                        🏆
                      </span>

                      {copy.wins}

                    </h3>


                    <div className="actor-palmares-list">

                      {wins.map(
                        (item) =>
                          renderEntry(
                            item,
                            "win"
                          )
                      )}

                    </div>

                  </section>

                )}


              {/* ===========================================
                NOMINATIONS
            =========================================== */}

              {nominations.length >
                0 && (

                  <section className="actor-palmares-section">

                    <h3>

                      <span
                        aria-hidden="true"
                      >
                        🎖️
                      </span>

                      {copy.nominations}

                    </h3>


                    <div className="actor-palmares-list">

                      {nominations.map(
                        (item) =>
                          renderEntry(
                            item,
                            "nomination"
                          )
                      )}

                    </div>

                  </section>

                )}


              {/* ===========================================
                SOURCE
            =========================================== */}

              {sourceLabel && (

                <p className="actor-palmares-popover__source">

                  {sourceLabel}

                </p>

              )}

            </>

          )}

      </div>

    </>
  );
};


export default ActorPalmaresPopover;